import { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-flexbox-grid";
import Search from "../images/search-icon.svg";
import { IClient, IUser, IPlaybook } from "../models";
import { ClientView } from "../pages/Resonate/Home";
import { Tooltip } from "antd";
import LoadingIcon from "../images/dual-ring-loading-white.svg";
import LoadingIconGrey from "../images/dual-ring-loading-grey.svg";
import ArrowLineLeft from "../images/arrow-left-line.svg";
import ArrowLineLeftBlack from "../images/arrow-left-line-black.svg";
import Button from "./Button";
import { auth, doGenerateClientReport } from "./Firebase";
import { DocumentOnePage } from "styled-icons/fluentui-system-regular";
import circleInfo from "../images/circle-info-light-white.svg";
import circleInfoBlack from "../images/circle-info-black.svg";
import { toast } from "react-toastify";
import UpgradeAccountModal from "./UpgradeAccountModal";
import { doLaunchCustomerPortal } from "./Firebase";
import { LoadingModal } from "./Resonate/Home/LoadingModal";
import { useNavigate } from "react-router";
import ThemeContext from "../contexts/ThemeContext";
import ImpactScore from "./Insight/ImpactScore";

interface HeroProps {
  user: IUser;
  view: ClientView;

  query: string;

  /* Client Management Page Props*/
  filterItems?: (filterQuery: string) => void;
  onEnterDuringSearch?: () => void;

  /* CLient Page Props */
  onOpenPlaybook?: () => void;
  onBack?: () => void;
  header?: React.ReactNode;
  activeClient?: IClient | null;
  clientScore?: number;
  clientRatio?: number;
  portfoliosReady?: boolean;
  displayRatio?: (ratio: number) => string;
  activePlaybook: IPlaybook | null;
  handleViewChange?: (view: ClientView) => void;
  toastIds?: Array<any>;
  setToastIds?: (toastIds: Array<any>) => void;
}

export default function Hero(props: HeroProps) {
  const [upgradeAccountModal, setUpgradeAccountModal] =
    useState<boolean>(false);
  const [loadingAccountModal, setLoadingAccountModal] =
    useState<boolean>(false);
  const [admin, setAdmin] = useState<boolean>(false);

  const { theme, setTheme } = useContext(ThemeContext);

  useEffect(() => {
    auth.currentUser &&
      auth.currentUser
        .getIdTokenResult()
        .then((idTokenResult) => {
          // Confirm the user is an Admin.
          if (!!idTokenResult.claims.admin) {
            setAdmin(true);
          }
        })
        .catch((error) => {
          console.log(error);
        });
  }, []);

  const navigate = useNavigate();
  return (
    <>
      <LoadingModal
        message="Loading Account Details"
        isActive={loadingAccountModal}
      />
      <UpgradeAccountModal
        isActive={upgradeAccountModal}
        title="Reports Dashboard Unavailable"
        message="To view reports dashboard, upgrade to the Essential plan."
        onConfirm={() => (
          setUpgradeAccountModal(false),
          setLoadingAccountModal(true),
          doLaunchCustomerPortal()
        )}
        onClose={() => {
          setUpgradeAccountModal(false);
        }}
      />
      <section
        className={
          props.view === ClientView.CLIENT_PAGE ||
          props.view === ClientView.CLIENT_MODELS ||
          props.view === ClientView.CLIENT_FUNDS ||
          props.view === ClientView.PROPOSALS
            ? "clientHeroContainer heroContainer"
            : "heroContainer"
        }
      >
        <div>
          {(props.view === ClientView.CLIENT_PAGE ||
            props.view === ClientView.CLIENT_MODELS ||
            props.view === ClientView.CLIENT_FUNDS ||
            props.view === ClientView.PROPOSALS) && (
            <div
              className="fullWidthContainer"
              style={{
                backgroundColor:
                  theme === "resonate" ? "rgba(255,255,255, 0.06)" : "#f5f5f5",
                position: "absolute",
                zIndex: 2,
              }}
            >
              <div className="fullWidthContent">
                <Row>
                  <Col>
                    <Button
                      preset="heroPrimary"
                      onClick={() => {
                        if (props.onBack) props.onBack();
                      }}
                    >
                      <img
                        src={
                          theme === "resonate"
                            ? ArrowLineLeft
                            : ArrowLineLeftBlack
                        }
                        width="20"
                        style={{
                          marginRight: 8,
                          position: "relative",
                          bottom: 2,
                        }}
                      />
                      Back to All Clients
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>
          )}
          <div
            className="fullWidthContent hero"
            style={{
              position: "relative",
            }}
          >
            {props.view === ClientView.CLIENT_PAGE ||
            props.view === ClientView.CLIENT_MODELS ||
            props.view === ClientView.CLIENT_FUNDS ||
            props.view === ClientView.PROPOSALS ? (
              <>
                <Row>
                  <Col xs={12} sm={theme === "resonate" ? 6 : 12}>
                    {theme === "insight" &&
                      props.activeClient?.portfolios[0] &&
                      (props.portfoliosReady ? (
                        <ImpactScore
                          type="large"
                          bgColor={"rgb(251, 239, 229)"}
                          score={props.clientScore?.toFixed(0)}
                          footer
                        />
                      ) : (
                        <div
                          style={{
                            float: "left",
                            display: "flex",
                            justifyContent: "center",
                            marginRight: 20,
                            width: 145,
                          }}
                        >
                          <img
                            width="70"
                            src={LoadingIconGrey}
                            style={{
                              position: "relative",
                              bottom: 7,
                              margin: "0 10px",
                            }}
                          />
                        </div>
                      ))}

                    <h1>
                      {props.activeClient &&
                        props.activeClient.name +
                          " " +
                          props.activeClient.lastName}
                    </h1>
                    {props.header ?? <></>}
                  </Col>

                  <Col xs={12} sm={6}>
                    {props.activeClient &&
                      props.activeClient?.portfolios[0] &&
                      (props.portfoliosReady ? (
                        theme === "resonate" ? (
                          <ul className="heroClientStats">
                            <li>
                              <Tooltip
                                title="The Alignment Percentage represents the percentage of an investment portfolio that does not have exposure to the issues a client has marked as negative in their custom Resonate screening profile, calculated according to portfolio weights of holdings. Unlike the Resonate Score, the Alignment Percentage does not factor in issues a client has marked as positive; it strictly measures avoidance of undesired exposures. The result is a percentage figure ranging from 0% to 100%, with higher percentages representing a potentially greater degree of alignment with the client's stated values. This metric seeks to evaluate values alignment only and should not be interpreted as an indicator of past or future investment performance."
                                placement="bottom"
                                color={
                                  theme === "resonate"
                                    ? "rgba(0, 108, 108, 0.9)"
                                    : "rgba(44, 138, 174, 0.9)"
                                }
                                overlayInnerStyle={{ width: 300, maxWidth: '100vw' }}
                              >
                                <h4>
                                  {props.clientScore
                                    ? props.clientScore.toFixed(0)
                                    : "NA"}
                                  <i>%</i>
                                </h4>

                                <span className="scoreLabel">
                                  Values Alignment
                                  <img
                                    style={{ marginTop: -3 }}
                                    src={
                                      theme === "resonate"
                                        ? circleInfo
                                        : circleInfoBlack
                                    }
                                    width="13"
                                  />
                                </span>
                              </Tooltip>
                            </li>
                            <li>
                              <Tooltip
                                title={"The Resonate Score seeks to provide investors with a numeric score that indicates how much or how little their investment portfolio aligns with the values they indicate are important to them on their Resonate custom screening profile. The Resonate Score algorithm assigns positive points for investments that 1) Align with the screening categories deemed positive by the client, and 2) Avoid the screening categories deemed negative by the client. Negative points are assigned for investments that 3) Align with screening categories deemed negative by the client, and 4) Do not align with screening categories deemed positive by the client. Individual investments and portfolios will likely have several positive and negative points that feed into the algorithm. If the positive points are equal to the negatives, then the Resonate Score will be 1 (a \"neutral\" score). If the positives outweigh the negatives, then the score will be between 1 and 2. If the negatives outweigh the positives, then the score will be between 0 and 1. Lower scores indicate worse alignment with the values clients indicate on their custom screening profile and higher scores indicate more alignment with the values clients indicate on their custom screening profile. Scores can range from zero (the worst possible score) to 2 (the best possible score). Scores above 1 can be viewed as \"favorable\" in terms of value alignment and scores less than 1 can be viewed as \"less favorable\" in terms of values alignment. Resonate Scores should not be used as an indicator of financial performance. Resonate Scores are limited by the breadth of data feeds that underly the scores, and there is no guarantee that the Resonate Score reflects every positive or negative issue existing in a company. The data may miss certain issues that individual investors may feel are important, or may incorporate certain issues which individual investors may not consider important. Please review the screening category list and definitions in the screening profile for more information about individual screening data points."}
                                placement="bottomLeft"
                                color={
                                  theme === "resonate"
                                    ? "rgba(0, 108, 108, 0.9)"
                                    : "rgba(44, 138, 174, 0.9)"
                                }
                                overlayInnerStyle={{ width: 400, maxWidth: '100vw' }}
                                overlayStyle={{ width: 400, maxWidth: '100vw' }}
                              >
                                <h4>
                                  {props.clientRatio && props.displayRatio
                                    ? props.displayRatio(props.clientRatio)
                                    : 0}
                                </h4>

                                <span className="scoreLabel">
                                  Resonate Score{" "}
                                  <img
                                    style={{ marginTop: -3 }}
                                    src={
                                      theme === "resonate"
                                        ? circleInfo
                                        : circleInfoBlack
                                    }
                                    width="13"
                                  />
                                </span>
                              </Tooltip>
                            </li>
                          </ul>
                        ) : (
                          <></>
                        )
                      ) : (
                        theme === "resonate" && (
                          <ul className="heroClientStats">
                            <li>
                              <h4>
                                <img
                                  width="70"
                                  src={
                                    theme === "resonate"
                                      ? LoadingIcon
                                      : LoadingIconGrey
                                  }
                                  style={{
                                    position: "relative",
                                    bottom: 7,
                                    margin: "0 10px",
                                  }}
                                />
                              </h4>

                              <span>Values Alignment</span>
                            </li>
                            <li>
                              <h4>
                                <img
                                  width="70"
                                  src={
                                    theme === "resonate"
                                      ? LoadingIcon
                                      : LoadingIconGrey
                                  }
                                  style={{
                                    position: "relative",
                                    bottom: 7,
                                    margin: "0 10px",
                                  }}
                                />
                              </h4>
                              <span>Resonate Score</span>
                            </li>
                          </ul>
                        )
                      ))}
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <h1>Welcome, {props.user.firstName}!</h1>
                <h4>{props.user.firm}</h4>
              </>
            )}
          </div>
        </div>

        <div className="heroNav">
          <div className="fullWidthContent">
            <Row>
              <Col
                xs={12}
                sm={theme !== "resonate" ? 12 : 6}
                md={theme !== "resonate" ? 12 : 8}
              >
                {props.view === ClientView.CLIENT_PAGE ||
                props.view === ClientView.CLIENT_MODELS ||
                props.view === ClientView.CLIENT_FUNDS ||
                props.view === ClientView.PROPOSALS ? (
                  <nav className="inlineNavigation">
                    <ul>
                      <li>
                        <a
                          className={
                            props.view === ClientView.CLIENT_PAGE
                              ? "active"
                              : ""
                          }
                          onClick={() => {
                            theme === "resonate"
                              ? navigate(`/`)
                              : navigate(`/dashboard`);
                            props.handleViewChange &&
                              props.handleViewChange(ClientView.CLIENT_PAGE);
                          }}
                        >
                          Portfolios
                        </a>
                      </li>
                      {theme === "resonate" && (
                        <>
                          <li className="hideOnMobile">
                            <a onClick={props.onOpenPlaybook}>
                              Screening Profile
                            </a>
                          </li>
                          <li>
                            <a
                              className={
                                props.view === ClientView.PROPOSALS
                                  ? "active"
                                  : ""
                              }
                              onClick={() => {
                                props.handleViewChange &&
                                  props.handleViewChange(ClientView.PROPOSALS);
                              }}
                            >
                              Proposals
                            </a>
                          </li>
                        </>
                      )}
                      <li>
                        <a
                          className={
                            props.view === ClientView.CLIENT_MODELS
                              ? "active"
                              : ""
                          }
                          onClick={() => {
                            props.handleViewChange &&
                              props.handleViewChange(ClientView.CLIENT_MODELS);
                          }}
                        >
                          Models
                        </a>
                      </li>
                      <li>
                        <a
                          className={
                            props.view === ClientView.CLIENT_FUNDS
                              ? "active"
                              : ""
                          }
                          onClick={() => {
                            props.handleViewChange &&
                              props.handleViewChange(ClientView.CLIENT_FUNDS);
                          }}
                        >
                          Faith-Based Funds
                        </a>
                      </li>
                    </ul>
                  </nav>
                ) : (
                  <>
                    <nav className="inlineNavigation">
                      <ul>
                        <li>
                          <a
                            className={
                              props.view === ClientView.CLIENT_MANAGEMENT
                                ? "active"
                                : ""
                            }
                            onClick={() => {
                              props.handleViewChange &&
                                props.handleViewChange(
                                  ClientView.CLIENT_MANAGEMENT
                                );
                            }}
                          >
                            Clients
                          </a>
                        </li>
                        <li>
                          <a
                            className={
                              props.view === ClientView.MODELS ? "active" : ""
                            }
                            onClick={() => {
                              props.handleViewChange &&
                                props.handleViewChange(ClientView.MODELS);
                            }}
                          >
                            Models
                          </a>
                        </li>
                        <li>
                          <a
                            className={
                              props.view === ClientView.FUNDS ? "active" : ""
                            }
                            onClick={() => {
                              props.handleViewChange &&
                                props.handleViewChange(ClientView.FUNDS);
                            }}
                          >
                            Faith-Based Funds
                          </a>
                        </li>
                        {theme === "resonate" && (
                          <>
                            <li>
                              <a
                                className={
                                  props.view === ClientView.REPORTS
                                    ? "active"
                                    : ""
                                }
                                onClick={() => {
                                  if (props.user.role === "essential") {
                                    props.handleViewChange &&
                                      props.handleViewChange(
                                        ClientView.REPORTS
                                      );
                                  } else {
                                    setUpgradeAccountModal(true);
                                  }
                                }}
                              >
                                Reports Archive
                              </a>
                            </li>
                          </>
                        )}
                        {theme === "insight" && (
                          <li>
                            <a
                              className={
                                props.view === ClientView.EXPORT_DATA
                                  ? "active"
                                  : ""
                              }
                              onClick={() => {
                                if (props.user.role === "essential") {
                                  props.handleViewChange &&
                                    props.handleViewChange(
                                      ClientView.EXPORT_DATA
                                    );
                                } else {
                                  setUpgradeAccountModal(true);
                                }
                              }}
                            >
                              Advanced Search
                            </a>
                          </li>
                        )}
                        {admin && (
                          <li>
                            <a
                              className={
                                props.view === ClientView.ADMIN ? "active" : ""
                              }
                              onClick={() => {
                                if (admin) {
                                  props.handleViewChange &&
                                    props.handleViewChange(ClientView.ADMIN);
                                }
                              }}
                            >
                              Admin
                            </a>
                          </li>
                        )}
                      </ul>
                    </nav>
                  </>
                )}
              </Col>
              {theme === "resonate" && (
                <Col xs={12} sm={6} md={4} className="alignRight">
                  {props.view === ClientView.CLIENT_PAGE ||
                  props.view === ClientView.PROPOSALS ? (
                    <>
                      <Button
                        preset="heroPrimary"
                        onClick={() => {
                          let requestId = Math.random().toString(16).slice(-4);
                          props.setToastIds &&
                            props.toastIds &&
                            props.activeClient &&
                            props.setToastIds({
                              ...props.toastIds,
                              [requestId]: toast.loading(
                                `Report for ${props.activeClient.name} household is generating`,
                                { closeOnClick: true }
                              ) as any,
                            });
                          props.activeClient &&
                            doGenerateClientReport(
                              props.activeClient,
                              props.activePlaybook,
                              requestId
                            );
                        }}
                      >
                        <DocumentOnePage
                          width="20"
                          style={{
                            marginRight: 8,
                            position: "relative",
                            bottom: 2,
                          }}
                        />
                        Create Household Report
                      </Button>
                    </>
                  ) : (
                    <input
                      type="text"
                      value={props.query}
                      placeholder={
                        props.view === ClientView.REPORTS
                          ? "Search Reports"
                          : props.view === ClientView.CLIENT_MANAGEMENT
                          ? "Search Clients"
                          : props.view === ClientView.FUNDS
                          ? "Search Faith-Based Funds"
                          : props.view === ClientView.CLIENT_FUNDS
                          ? "Search Faith-Based Funds"
                          : "Search Models"
                      }
                      onKeyDown={(event) => {
                        if (
                          event.key == "Enter" &&
                          props.view !== ClientView.REPORTS
                        ) {
                          props.onEnterDuringSearch &&
                            props.onEnterDuringSearch();
                        }
                      }}
                      className="searchClients"
                      style={{
                        background: `url(${Search})`,
                        backgroundPosition: "95% 0.7em",
                        backgroundSize: 20,
                        backgroundRepeat: "no-repeat",
                        backgroundColor: "rgba(213, 232, 239, 0.4)",
                      }}
                      onChange={(e) => {
                        props.filterItems && props.filterItems(e.target.value);
                      }}
                    />
                  )}
                </Col>
              )}
            </Row>
          </div>
        </div>
      </section>
    </>
  );
}
